/**
 * Ficha de um equipamento (`/catalogo/:id`) — identidade editável, o painel de
 * preço com "o histórico de preço como registro de calibração" (docs/DESIGN.md,
 * o segundo dos dois momentos de ousadia do sistema) e os acessórios.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError } from '@/lib/api';
import { deltaTone, fmtDate, fmtDeltaPct } from '@/lib/format';
import type { PriceVersion } from '@/lib/types';
import {
  fracaoParaPercentTexto,
  hojeISO,
  parseValorMonetario,
  percentTextoParaFracao,
  useAtualizarProduto,
  useAtualizarVersaoPreco,
  useCriarVersaoPreco,
  useExcluirProduto,
  useExcluirVersaoPreco,
  useProduto,
  useSalvarAcessorios,
  type ProdutoCatalogoDetalhe,
} from '@/hooks/useCatalogo';
import { useToast } from '@/hooks/useToast';
import { PageHeader } from '@/components/PageHeader';
import { Panel } from '@/components/Panel';
import { Field } from '@/components/Field';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Badge } from '@/components/Badge';
import { Money } from '@/components/Money';
import { Percent } from '@/components/Percent';
import { Spinner } from '@/components/Spinner';
import { EmptyState } from '@/components/EmptyState';
import './CatalogoDetalhe.css';

/** Formata um número para o texto editável em pt-BR ("24829.2" → "24.829,20"). */
function formatValorEditavel(n: number): string {
  return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function CatalogoDetalhe() {
  const { id } = useParams();
  const produtoId = Number(id);
  const produtoQuery = useProduto(Number.isFinite(produtoId) ? produtoId : undefined);

  if (produtoQuery.isLoading) {
    return (
      <div className="cd-loading">
        <Spinner label="Carregando equipamento…" />
      </div>
    );
  }

  if (!produtoQuery.data) {
    return (
      <EmptyState
        title="Equipamento não encontrado"
        description="Ele pode ter sido excluído do catálogo."
        action={
          <Link to="/catalogo">
            <Button>Voltar ao catálogo</Button>
          </Link>
        }
      />
    );
  }

  const produto = produtoQuery.data;

  return (
    <>
      <PageHeader
        title={produto.name}
        meta={
          <Link to="/catalogo" className="muted">
            Voltar ao catálogo
          </Link>
        }
        actions={<Badge variant={produto.active ? 'positive' : 'neutral'}>{produto.active ? 'Ativo' : 'Inativo'}</Badge>}
      />
      <div className="stack-lg">
        <PainelIdentidade produto={produto} />
        <PainelPreco produto={produto} />
        <PainelAcessorios produto={produto} />
      </div>
    </>
  );
}

// ------------------------------------------------------------- identificação --

function PainelIdentidade({ produto }: { produto: ProdutoCatalogoDetalhe }) {
  const toast = useToast();
  const navigate = useNavigate();
  const atualizar = useAtualizarProduto();
  const excluir = useExcluirProduto();

  const [nome, setNome] = useState(produto.name);
  const [detalhes, setDetalhes] = useState(produto.details);
  const [ativo, setAtivo] = useState(produto.active);
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  const [erros, setErros] = useState<Record<string, string>>({});
  const [confirmandoExclusao, setConfirmandoExclusao] = useState(false);
  const [erroExclusao, setErroExclusao] = useState<string | null>(null);

  useEffect(() => {
    setNome(produto.name);
    setDetalhes(produto.details);
    setAtivo(produto.active);
    // Só reinicia quando troca de produto — não a cada refetch do mesmo id,
    // senão uma edição em andamento aqui seria apagada por um save no painel de acessórios.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [produto.id]);

  const dirty = nome !== produto.name || detalhes !== produto.details || ativo !== produto.active;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setErroGeral(null);
    setErros({});
    try {
      await atualizar.mutateAsync({ id: produto.id, input: { name: nome, details: detalhes, active: ativo } });
      toast.success('Equipamento atualizado.');
    } catch (err) {
      if (err instanceof ApiError) {
        setErroGeral(err.message);
        setErros(err.fields ?? {});
      } else {
        setErroGeral('Não foi possível salvar agora. Tente de novo.');
      }
    }
  }

  async function handleExcluir() {
    setErroExclusao(null);
    try {
      await excluir.mutateAsync(produto.id);
      toast.success(`${produto.name} excluído.`);
      navigate('/catalogo');
    } catch (err) {
      setErroExclusao(err instanceof ApiError ? err.message : 'Não foi possível excluir agora.');
    }
  }

  return (
    <Panel title="Identificação">
      <form className="stack" onSubmit={handleSubmit} noValidate>
        {erroGeral ? (
          <p className="form-alert form-alert-error" role="alert">
            {erroGeral}
          </p>
        ) : null}
        <Field label="Nome" required value={nome} onChange={(e) => setNome(e.target.value)} error={erros.name} />
        <Field
          as="textarea"
          label="Detalhes"
          value={detalhes}
          onChange={(e) => setDetalhes(e.target.value)}
          error={erros.details}
        />
        <label className="checkbox">
          <input type="checkbox" checked={ativo} onChange={(e) => setAtivo(e.target.checked)} />
          Equipamento ativo
        </label>
        <div className="row row-between">
          <Button type="button" variant="danger" onClick={() => setConfirmandoExclusao(true)}>
            Excluir equipamento
          </Button>
          <Button type="submit" disabled={!dirty || atualizar.isPending}>
            {atualizar.isPending ? 'Salvando…' : 'Salvar alterações'}
          </Button>
        </div>
      </form>

      <ConfirmDialog
        open={confirmandoExclusao}
        title="Excluir equipamento"
        tone="danger"
        confirmLabel="Excluir"
        loading={excluir.isPending}
        onCancel={() => {
          setConfirmandoExclusao(false);
          setErroExclusao(null);
        }}
        onConfirm={handleExcluir}
        message={
          <div className="stack-sm">
            <p>
              Excluir <strong>{produto.name}</strong>? Esta ação não pode ser desfeita.
            </p>
            {erroExclusao ? (
              <p className="form-alert form-alert-error" role="alert">
                {erroExclusao}
              </p>
            ) : null}
          </div>
        }
      />
    </Panel>
  );
}

// ------------------------------------------------------------------- preço --

function PainelPreco({ produto }: { produto: ProdutoCatalogoDetalhe }) {
  const [modalAberto, setModalAberto] = useState(false);

  return (
    <Panel title="Preço" actions={<Button onClick={() => setModalAberto(true)}>Registrar reajuste</Button>}>
      {produto.currentPrice ? (
        <div className="cd-price__current">
          <Money value={produto.currentPrice.unitValue} className="cd-price__value" />
          <p className="cd-price__meta muted">
            vigente desde {fmtDate(produto.currentPrice.validFrom)} · IPI <Percent value={produto.currentPrice.ipi} />
          </p>
        </div>
      ) : (
        <div className="cd-price__current">
          <Badge variant="attention">Sem preço vigente</Badge>
          <p className="cd-price__meta muted">
            {produto.nextPrice
              ? `Este equipamento não aparece em Nova Proposta até ${fmtDate(produto.nextPrice.validFrom)}.`
              : 'Este equipamento não tem nenhuma versão de preço vigente e não aparece em Nova Proposta.'}
          </p>
        </div>
      )}

      <HistoricoPreco produto={produto} />

      <ModalReajuste produto={produto} open={modalAberto} onClose={() => setModalAberto(false)} />
    </Panel>
  );
}

/** O "registro de calibração": linha do tempo vertical das versões de preço. */
function HistoricoPreco({ produto }: { produto: ProdutoCatalogoDetalhe }) {
  const toast = useToast();
  const excluirVersao = useExcluirVersaoPreco();
  const [versaoEditando, setVersaoEditando] = useState<PriceVersion | null>(null);
  const [versaoEditandoNota, setVersaoEditandoNota] = useState<PriceVersion | null>(null);
  const [versaoExcluindo, setVersaoExcluindo] = useState<PriceVersion | null>(null);
  const [erroExclusao, setErroExclusao] = useState<string | null>(null);

  // Já vem "mais recente primeiro" — futuras no topo, vigente em seguida, passadas depois.
  const versoes = produto.priceVersions;

  async function confirmarExclusao() {
    if (!versaoExcluindo) return;
    setErroExclusao(null);
    try {
      await excluirVersao.mutateAsync({ productId: produto.id, versionId: versaoExcluindo.id });
      toast.success('Versão de preço excluída.');
      setVersaoExcluindo(null);
    } catch (err) {
      setErroExclusao(err instanceof ApiError ? err.message : 'Não foi possível excluir agora.');
    }
  }

  return (
    <div className="calib">
      <p className="cd-subheading">Histórico de preço</p>
      <ol className="calib__list">
        {versoes.map((v) => {
          const tom = deltaTone(v.deltaPct);
          return (
            <li key={v.id} className={`calib__item${v.isFuture ? ' calib__item--future' : ''}`}>
              <span className={`calib__tick${v.isFuture ? ' calib__tick--vazada' : ''}`} aria-hidden="true" />
              <div className="calib__row">
                <span className="calib__date mono">{fmtDate(v.validFrom)}</span>
                <Money value={v.unitValue} className="calib__value" />
                {v.deltaPct !== null ? (
                  <span className={`calib__delta${tom !== 'neutral' ? ` tone-${tom}` : ''}`}>{fmtDeltaPct(v.deltaPct)}</span>
                ) : null}
                {v.inUse ? <IconeCadeado /> : null}
              </div>
              {v.isFuture ? <p className="calib__future-label">entra em vigor em {fmtDate(v.validFrom)}</p> : null}
              <p className="calib__meta muted">
                IPI <Percent value={v.ipi} /> · {v.createdBy ? `registrado por ${v.createdBy.name}` : 'registro inicial'}
              </p>
              {v.note ? <p className="calib__note">{v.note}</p> : null}
              <div className="calib__actions">
                {v.inUse ? (
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => setVersaoEditandoNota(v)}>
                    Editar nota
                  </button>
                ) : (
                  <>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => setVersaoEditando(v)}>
                      Editar
                    </button>
                    {versoes.length > 1 ? (
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => {
                          setErroExclusao(null);
                          setVersaoExcluindo(v);
                        }}
                      >
                        Excluir
                      </button>
                    ) : null}
                  </>
                )}
              </div>
            </li>
          );
        })}
      </ol>

      {versaoEditando ? (
        <ModalEditarVersao produto={produto} versao={versaoEditando} onClose={() => setVersaoEditando(null)} />
      ) : null}
      {versaoEditandoNota ? (
        <ModalEditarNota produto={produto} versao={versaoEditandoNota} onClose={() => setVersaoEditandoNota(null)} />
      ) : null}

      <ConfirmDialog
        open={versaoExcluindo !== null}
        title="Excluir versão de preço"
        tone="danger"
        confirmLabel="Excluir"
        loading={excluirVersao.isPending}
        onCancel={() => {
          setVersaoExcluindo(null);
          setErroExclusao(null);
        }}
        onConfirm={confirmarExclusao}
        message={
          <div className="stack-sm">
            <p>Excluir a versão de {versaoExcluindo ? fmtDate(versaoExcluindo.validFrom) : ''}?</p>
            {erroExclusao ? (
              <p className="form-alert form-alert-error" role="alert">
                {erroExclusao}
              </p>
            ) : null}
          </div>
        }
      />
    </div>
  );
}

function IconeCadeado() {
  return (
    <span className="calib__lock" title="Já usada em uma proposta — não pode ser alterada, só a nota.">
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <rect x="5" y="11" width="14" height="10" rx="2" />
        <path d="M8 11V7a4 4 0 0 1 8 0v4" />
      </svg>
      <span className="sr-only">Usada em uma proposta — não pode ser editada</span>
    </span>
  );
}

// -------------------------------------------------------- modal · reajuste --

function ModalReajuste({
  produto,
  open,
  onClose,
}: {
  produto: ProdutoCatalogoDetalhe;
  open: boolean;
  onClose: () => void;
}) {
  const toast = useToast();
  const criar = useCriarVersaoPreco();
  const baseline = produto.currentPrice?.unitValue ?? produto.priceVersions.find((v) => !v.isFuture)?.unitValue ?? null;

  const [valor, setValor] = useState('');
  const [ipi, setIpi] = useState('0,00');
  const [validade, setValidade] = useState(hojeISO());
  const [nota, setNota] = useState('');
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  const [erros, setErros] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) return;
    setValor('');
    setIpi(fracaoParaPercentTexto(produto.currentPrice?.ipi ?? 0));
    setValidade(hojeISO());
    setNota('');
    setErroGeral(null);
    setErros({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const valorNumerico = valor.trim() === '' ? null : parseValorMonetario(valor);
  const variacao = valorNumerico !== null && baseline ? (valorNumerico - baseline) / baseline : null;
  const tom = variacao === null ? 'neutral' : deltaTone(variacao);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setErroGeral(null);
    setErros({});
    try {
      await criar.mutateAsync({
        productId: produto.id,
        input: {
          unitValue: parseValorMonetario(valor),
          ipi: percentTextoParaFracao(ipi),
          validFrom: validade || undefined,
          note: nota || undefined,
        },
      });
      toast.success('Reajuste registrado.');
      onClose();
    } catch (err) {
      if (err instanceof ApiError) {
        setErroGeral(err.message);
        setErros(err.fields ?? {});
      } else {
        setErroGeral('Não foi possível registrar agora. Tente de novo.');
      }
    }
  }

  return (
    <Modal
      open={open}
      title="Registrar reajuste"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={criar.isPending}>
            Cancelar
          </Button>
          <Button type="submit" form="form-reajuste" disabled={criar.isPending}>
            {criar.isPending ? 'Registrando…' : 'Registrar reajuste'}
          </Button>
        </>
      }
    >
      <form id="form-reajuste" className="stack" onSubmit={handleSubmit} noValidate>
        {erroGeral ? (
          <p className="form-alert form-alert-error" role="alert">
            {erroGeral}
          </p>
        ) : null}
        <Field
          label="Novo valor (R$)"
          required
          className="mono"
          inputMode="decimal"
          value={valor}
          onChange={(e) => setValor(e.target.value)}
          error={erros.unitValue}
          hint="Use vírgula para os centavos, ex.: 26000,00."
        />
        {variacao !== null ? (
          <p className={`cd-variacao${tom !== 'neutral' ? ` tone-${tom}` : ''}`}>
            Variação sobre o preço vigente: <strong>{fmtDeltaPct(variacao)}</strong>
          </p>
        ) : baseline === null ? (
          <p className="muted">Este equipamento ainda não tem preço vigente para comparar.</p>
        ) : null}
        <div className="field-grid">
          <Field label="IPI (%)" className="mono" inputMode="decimal" value={ipi} onChange={(e) => setIpi(e.target.value)} error={erros.ipi} />
          <Field
            label="Vigência a partir de"
            type="date"
            value={validade}
            onChange={(e) => setValidade(e.target.value)}
            error={erros.validFrom}
            hint="Pode ser futura — vira um reajuste agendado."
          />
        </div>
        <Field
          as="textarea"
          label="Nota"
          value={nota}
          onChange={(e) => setNota(e.target.value)}
          error={erros.note}
          hint="Explique o motivo do reajuste."
        />
      </form>
    </Modal>
  );
}

// --------------------------------------------------- modal · editar versão --

function ModalEditarVersao({
  produto,
  versao,
  onClose,
}: {
  produto: ProdutoCatalogoDetalhe;
  versao: PriceVersion;
  onClose: () => void;
}) {
  const toast = useToast();
  const atualizar = useAtualizarVersaoPreco();

  const [valor, setValor] = useState(() => formatValorEditavel(versao.unitValue));
  const [ipi, setIpi] = useState(() => fracaoParaPercentTexto(versao.ipi));
  const [validade, setValidade] = useState(versao.validFrom);
  const [nota, setNota] = useState(versao.note ?? '');
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  const [erros, setErros] = useState<Record<string, string>>({});

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setErroGeral(null);
    setErros({});
    try {
      await atualizar.mutateAsync({
        productId: produto.id,
        versionId: versao.id,
        input: {
          unitValue: parseValorMonetario(valor),
          ipi: percentTextoParaFracao(ipi),
          validFrom: validade,
          note: nota || undefined,
        },
      });
      toast.success('Versão de preço atualizada.');
      onClose();
    } catch (err) {
      if (err instanceof ApiError) {
        setErroGeral(err.message);
        setErros(err.fields ?? {});
      } else {
        setErroGeral('Não foi possível salvar agora. Tente de novo.');
      }
    }
  }

  return (
    <Modal
      open
      title="Editar versão de preço"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={atualizar.isPending}>
            Cancelar
          </Button>
          <Button type="submit" form="form-editar-versao" disabled={atualizar.isPending}>
            {atualizar.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </>
      }
    >
      <form id="form-editar-versao" className="stack" onSubmit={handleSubmit} noValidate>
        {erroGeral ? (
          <p className="form-alert form-alert-error" role="alert">
            {erroGeral}
          </p>
        ) : null}
        <Field
          label="Valor (R$)"
          required
          className="mono"
          inputMode="decimal"
          value={valor}
          onChange={(e) => setValor(e.target.value)}
          error={erros.unitValue}
        />
        <div className="field-grid">
          <Field label="IPI (%)" className="mono" inputMode="decimal" value={ipi} onChange={(e) => setIpi(e.target.value)} error={erros.ipi} />
          <Field
            label="Vigência a partir de"
            type="date"
            value={validade}
            onChange={(e) => setValidade(e.target.value)}
            error={erros.validFrom}
          />
        </div>
        <Field as="textarea" label="Nota" value={nota} onChange={(e) => setNota(e.target.value)} error={erros.note} />
      </form>
    </Modal>
  );
}

// ----------------------------------------------------- modal · editar nota --

function ModalEditarNota({
  produto,
  versao,
  onClose,
}: {
  produto: ProdutoCatalogoDetalhe;
  versao: PriceVersion;
  onClose: () => void;
}) {
  const toast = useToast();
  const atualizar = useAtualizarVersaoPreco();
  const [nota, setNota] = useState(versao.note ?? '');
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  const [erros, setErros] = useState<Record<string, string>>({});

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setErroGeral(null);
    setErros({});
    try {
      await atualizar.mutateAsync({ productId: produto.id, versionId: versao.id, input: { note: nota || undefined } });
      toast.success('Nota atualizada.');
      onClose();
    } catch (err) {
      if (err instanceof ApiError) {
        setErroGeral(err.message);
        setErros(err.fields ?? {});
      } else {
        setErroGeral('Não foi possível salvar agora. Tente de novo.');
      }
    }
  }

  return (
    <Modal
      open
      title="Editar nota da versão"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={atualizar.isPending}>
            Cancelar
          </Button>
          <Button type="submit" form="form-editar-nota" disabled={atualizar.isPending}>
            {atualizar.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </>
      }
    >
      <form id="form-editar-nota" className="stack" onSubmit={handleSubmit} noValidate>
        <p className="muted">
          Esta versão ({fmtDate(versao.validFrom)}, <Money value={versao.unitValue} />) já foi usada em uma proposta —
          o valor, o IPI e a data de vigência não podem mais mudar. Só a nota.
        </p>
        {erroGeral ? (
          <p className="form-alert form-alert-error" role="alert">
            {erroGeral}
          </p>
        ) : null}
        <Field as="textarea" label="Nota" value={nota} onChange={(e) => setNota(e.target.value)} error={erros.note} />
      </form>
    </Modal>
  );
}

// -------------------------------------------------------------- acessórios --

function PainelAcessorios({ produto }: { produto: ProdutoCatalogoDetalhe }) {
  const toast = useToast();
  const salvar = useSalvarAcessorios();
  const [itens, setItens] = useState<string[]>(produto.accessories);

  useEffect(() => {
    setItens(produto.accessories);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [produto.id]);

  const dirty = JSON.stringify(itens) !== JSON.stringify(produto.accessories);

  function atualizarItem(indice: number, valor: string) {
    setItens((prev) => prev.map((v, i) => (i === indice ? valor : v)));
  }
  function remover(indice: number) {
    setItens((prev) => prev.filter((_, i) => i !== indice));
  }
  function adicionar() {
    setItens((prev) => [...prev, '']);
  }
  function mover(indice: number, direcao: -1 | 1) {
    setItens((prev) => {
      const alvo = indice + direcao;
      if (alvo < 0 || alvo >= prev.length) return prev;
      const nova = prev.slice();
      [nova[indice], nova[alvo]] = [nova[alvo], nova[indice]];
      return nova;
    });
  }

  async function salvarLista() {
    try {
      await salvar.mutateAsync({ productId: produto.id, items: itens.map((s) => s.trim()).filter((s) => s.length > 0) });
      toast.success('Acessórios atualizados.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não foi possível salvar agora.');
    }
  }

  return (
    <Panel
      title="Acessórios"
      actions={
        <Button size="sm" variant="ghost" onClick={adicionar}>
          Adicionar acessório
        </Button>
      }
    >
      {itens.length === 0 ? (
        <p className="muted">Nenhum acessório cadastrado.</p>
      ) : (
        <ul className="stack-sm cd-acessorios">
          {itens.map((valor, indice) => (
            <li key={indice} className="row cd-acessorio-item">
              <div className="cd-reorder">
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  aria-label="Mover acessório para cima"
                  disabled={indice === 0}
                  onClick={() => mover(indice, -1)}
                >
                  ▲
                </button>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  aria-label="Mover acessório para baixo"
                  disabled={indice === itens.length - 1}
                  onClick={() => mover(indice, 1)}
                >
                  ▼
                </button>
              </div>
              <input
                className="input"
                value={valor}
                onChange={(e) => atualizarItem(indice, e.target.value)}
                aria-label={`Acessório ${indice + 1}`}
              />
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => remover(indice)}>
                Remover
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="row row-end cd-acessorios-footer">
        <Button onClick={salvarLista} disabled={!dirty || salvar.isPending}>
          {salvar.isPending ? 'Salvando…' : 'Salvar acessórios'}
        </Button>
      </div>
    </Panel>
  );
}
